import { Card } from '@/components/ui/Card'
import { SectionHeader } from '@/components/ui/SectionHeader'
import { Segmented } from '@/components/ui/Segmented'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { Check, Minus } from 'lucide-react'

/** GET /accounting/settings (docs/accounting.md «Настройки учёта»). */
export interface AccountingSettings {
  /** immediate (default) | ratable — fixed per payment at payment time */
  subscription_access_recognition: 'immediate' | 'ratable'
  failed_jobs_cost_enabled: boolean
  updated_by: number | null
  updated_at: string | null
  can_edit: boolean
}

export interface AccountingSettingsCardProps {
  /** undefined while loading */
  data?: AccountingSettings
  busy?: boolean
  error?: string | null
  onChange: (patch: Partial<Pick<AccountingSettings, 'subscription_access_recognition' | 'failed_jobs_cost_enabled'>>) => void
}

const RECOGNITION = [
  { value: 'immediate', label: 'Сразу при оплате' },
  { value: 'ratable', label: 'По дням периода' },
] as const

const FAILED = [
  { value: 'on', label: 'Учитывать' },
  { value: 'off', label: 'Не учитывать' },
] as const

/**
 * Policy switches of the accounting, owner only (audited): when the access part of a subscription
 * becomes revenue — at payment (the owner's default) or day by day over the paid period (the
 * unrecognized part is the liability «Отложенная выручка по подпискам»); a switch applies only to
 * payments paid from now on. And whether the P&L books the ESTIMATED provider cost of failed
 * generations. The current value is also said in words, not only by the selected segment.
 */
export function AccountingSettingsCard({ data, busy, error, onChange }: AccountingSettingsCardProps) {
  const header = (
    <SectionHeader as="h3" title="Настройки учёта" description="Меняет только владелец; каждое изменение в аудите." />
  )
  if (data === undefined) return <section>{header}</section>
  const ratable = data.subscription_access_recognition === 'ratable'
  const disabled = !data.can_edit || busy
  return (
    <section>
      {header}
      <Card>
        <div className="flex flex-col gap-4 text-body">
          <div className="flex flex-col gap-2">
            <Segmented
              label="Выручка доступа по подпискам"
              value={data.subscription_access_recognition}
              options={RECOGNITION}
              disabled={disabled}
              onChange={(v) => onChange({ subscription_access_recognition: v })}
            />
            <p className="text-caption text-ink-3">
              {ratable
                ? 'Сейчас: по дням оплаченного периода. Непризнанная часть — обязательство «Отложенная выручка по подпискам».'
                : 'Сейчас: сразу при оплате.'}{' '}
              Переключение действует только на новые оплаты; уже оплаченные подписки остаются как были учтены.
            </p>
          </div>
          <div className="flex flex-col gap-2">
            <Segmented
              label="Себестоимость неудачных генераций"
              value={data.failed_jobs_cost_enabled ? 'on' : 'off'}
              options={FAILED}
              disabled={disabled}
              onChange={(v) => onChange({ failed_jobs_cost_enabled: v === 'on' })}
            />
            <p className="flex flex-wrap items-center gap-2 text-caption text-ink-3">
              {data.failed_jobs_cost_enabled ? (
                <StatusBadge tone="warn" icon={<Check />}>
                  Учитывается, оценка
                </StatusBadge>
              ) : (
                <StatusBadge tone="neutral" icon={<Minus />}>
                  Не учитывается
                </StatusBadge>
              )}
              Цена по котировке за задачи, которые провайдер принял, но они не дали результата.
            </p>
          </div>
          {error && (
            <p role="alert" className="text-caption text-danger [overflow-wrap:anywhere]">
              <span aria-hidden>✕ </span>
              {error}
            </p>
          )}
          {!data.can_edit && <p className="text-caption text-ink-3">Настройки меняет только владелец.</p>}
        </div>
      </Card>
    </section>
  )
}
